/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: VoiceLanguageSelect.tsx
 🗓️ Created: 2025-01-05
 🧩 Description:
    Custom voice language selection component for telephony systems,
    with search functionality and FreeSWITCH integration support.

 🔧 Notes:
    - Implements searchable dropdown with 5-item display limit
    - Supports keyboard navigation (arrow keys, enter, escape)
    - Includes comprehensive voice language options for telephony
    - Optimized for performance with filtered results
    - Integrates with FreeSWITCH voice guide systems

 🔁 Last Updated: 2025-01-05 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import React, { useState, useMemo } from 'react';
import { ChevronDown, Search, Mic } from 'lucide-react';
import { languageOptions } from '../../data/formOptions';

interface VoiceLanguageSelectProps {
  value: string;
  onChange: (value: string) => void;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
}

export const VoiceLanguageSelect: React.FC<VoiceLanguageSelectProps> = ({
  value,
  onChange,
  className = '',
  placeholder = 'Select voice language...',
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Filtrar opciones basado en la búsqueda
  const filteredOptions = useMemo(() => {
    const allOptions = [
      { value: 'all', label: 'All Voice Languages' },
      ...languageOptions
    ];

    if (!searchQuery.trim()) {
      return allOptions;
    }
    
    const query = searchQuery.toLowerCase();
    return allOptions.filter(option => 
      option.label.toLowerCase().includes(query) ||
      option.value.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  // Obtener la etiqueta del valor seleccionado
  const selectedLabel = useMemo(() => {
    const allOptions = [
      { value: 'all', label: 'All Voice Languages' },
      ...languageOptions
    ];
    const selected = allOptions.find(option => option.value === value);
    return selected ? selected.label : placeholder;
  }, [value, placeholder]);

  // Manejar selección
  const handleSelect = (languageValue: string) => {
    onChange(languageValue);
    setIsOpen(false);
    setSearchQuery('');
  };

  // Manejar teclado
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchQuery('');
    }
  };

  return (
    <div className="relative">
      {/* Trigger Button */}
      <button
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        disabled={disabled}
        className={`
          w-full px-3 py-2 text-sm border rounded bg-background text-foreground 
          focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent
          flex items-center justify-between gap-2
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-muted/50'}
          ${isOpen ? 'ring-1 ring-primary border-transparent' : 'border-border'}
          ${className}
        `}
      >
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Mic className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <span className="truncate text-left">
            {selectedLabel}
          </span>
        </div>
        <ChevronDown 
          className={`h-4 w-4 text-muted-foreground transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`} 
        />
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-80 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-border">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search voice languages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-9 pr-3 py-2 text-sm border border-border rounded bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-transparent"
                autoFocus
              />
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-60 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-3 py-2 text-sm text-muted-foreground text-center">
                No voice languages found
              </div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => handleSelect(option.value)}
                  className={`
                    w-full px-3 py-2 text-sm text-left hover:bg-muted transition-colors
                    flex items-center gap-2
                    ${value === option.value ? 'bg-primary/10 text-primary' : 'text-foreground'}
                  `}
                >
                  <Mic className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                  <span className="truncate">{option.label}</span>
                </button>
              ))
            )}
          </div>

          {/* Footer with count */}
          {filteredOptions.length > 0 && (
            <div className="px-3 py-2 text-xs text-muted-foreground border-t border-border bg-muted/30">
              {filteredOptions.length} voice language{filteredOptions.length !== 1 ? 's' : ''} found
            </div>
          )}
        </div>
      )}

      {/* Backdrop */}
      {isOpen && (
        <div 
          className="fixed inset-0 z-40" 
          onClick={() => {
            setIsOpen(false);
            setSearchQuery('');
          }}
        />
      )}
    </div>
  );
};

export default VoiceLanguageSelect;
